import { Nullable } from 'types'

export interface SkillEntry {
  skill_id: string
  name: string
  required?: boolean
  required_years?: number
  group_index?: number
  group_additional_skills?: Omit<SkillEntry, 'group_additional_skills'>[]
}

// this input is used for stuff like JobSkills and UserSkills, i.e. with the join models as its values
// we only care about the skill_id
export interface SkillInputValueItem {
  skill_id: string
  name: string
  required?: boolean
  required_years?: number
  group_index?: number
}

// for auto-complete suggestions/options, we use actual Skills (i.e. the Skill model), and use the ID from it
export interface SkillInputOption {
  // TODO: this looks like a mixup between id and skill_id, one of them should be removed
  id?: Nullable<string>
  skill_id?: Nullable<string>
  name: string
}

/**
 * Given an Array of skills, creates a Map where each entry is an array of skills in their respective group.
 */
export function mapSkillGroups(skills: SkillEntry[]): Map<number, SkillEntry[]> {
  const groups = new Map<number, SkillEntry[]>()

  for (let skill of skills) {
    const skillGroupIndex = skill.group_index || 0

    if (!groups.has(skillGroupIndex)) {
      groups.set(skillGroupIndex, [])
    }

    groups.get(skillGroupIndex).push(skill)
  }

  return groups
}

/**
 * Given an Array of group indexed skills (representing alternative sets), creates an array of skills with skill-contained alternatives.
 */
export function mergeSkillGroups(skills: SkillEntry[]): SkillEntry[] {
  const groups = mapSkillGroups(skills)
  const groupIndices = Array.from(groups.keys())
  const mergedSkills: SkillEntry[] = []

  for (let groupIndex of groupIndices) {
    for (let i = 0; i < groups.get(groupIndex).length; i++) {
      const skill = groups.get(groupIndex)[i]
      const mergedSkill = mergedSkills[i]

      if (mergedSkill) {
        if (mergedSkill.skill_id === skill.skill_id) {
          // same skill, merge by taking the greater of required years
          mergedSkills[i] = {
            ...mergedSkill,
            required_years: Math.max(mergedSkill.required_years || 0, skill.required_years || 0),
          }
        } else if (!mergedSkill.group_additional_skills.some(altSkill => altSkill.skill_id === skill.skill_id)) {
          mergedSkill.group_additional_skills = [...mergedSkill.group_additional_skills, skill]
        }
      } else {
        mergedSkills[i] = {
          ...skill,
          group_additional_skills: [],
        }
      }
    }
  }

  return mergedSkills
}

function* buildSkillGroups(skills: SkillEntry[]): Generator<SkillEntry[]> {
  // create a group for each logical permutation of possible combinations after projection
  // simplest to do this recursively
  if (skills.length === 0) {
    // termination condition met, checked for one level up
    return
  }

  const head = skills[0]
  const headSize = (head.group_additional_skills?.length || 0) + 1

  for (let i = 0; i < headSize; i++) {
    const skill = (i === 0) ? head : head.group_additional_skills[i - 1]
    const groups = Array.from(buildSkillGroups(skills.slice(1)))

    if (groups.length > 0) {
      for (let group of groups) {
        yield [skill, ...group]
      }
    } else {
      yield [skill]
    }
  }
}

/**
 * Given an Array of skills with additional skills as acceptable alternatives, constructs the skill group sets.
 * @param skills The skills to group.
 * @returns A Generator yielding a sequence of skills.
 */
export function* createSkillGroups(skills: SkillEntry[]): Generator<SkillEntry> {
  const groups = Array.from(buildSkillGroups(skills))

  for (let i = 0; i < groups.length; i++) {
    for (let skill of groups[i]) {
      yield {
        ...skill,
        group_index: i,
      }
    }
  }
}

/**
 * Gets a skill's label with the required years and contained alternatives also included.
 */
export function getSkillLabel(skill: SkillEntry): string {
  let label = skill.name

  if (skill.required_years) {
    label += ` (${skill.required_years}+ yrs)`
  }

  if (skill.group_additional_skills) {
    for (let altSkill of skill.group_additional_skills) {
      label += ` OR ${getSkillLabel(altSkill)}`
    }
  }

  return label
}
